/**
 * Componente de indicador de carga con accesibilidad completa
 * 
 * @description Proporciona feedback visual y accesible durante operaciones asíncronas.
 * Implementa las mejores prácticas de accesibilidad web para estados de carga,
 * garantizando que todos los usuarios reciban notificación apropiada del estado
 * de la aplicación sin interrumpir su experiencia de navegación.
 * 
 * **Características de accesibilidad:**
 * - `role="status"` identifica el elemento como indicador de estado
 * - `aria-live="polite"` anuncia cambios sin interrumpir lectores de pantalla
 * - `aria-hidden="true"` en indicador visual para evitar redundancia
 * - Cumple con guías WCAG 2.1 para elementos de estado
 * 
 * **Casos de uso:**
 * - Carga de resultados de búsqueda
 * - Peticiones a APIs externas
 * - Procesamiento de formularios
 * - Navegación entre páginas
 * - Cualquier operación asíncrona
 * 
 * **Diseño visual:**
 * - Centrado en pantalla para máxima visibilidad
 * - Indicador animado no intrusivo
 * - Compatible con temas y estilos personalizados
 * - Responsive para todos los dispositivos
 * 
 * @component
 * @returns {JSX.Element} Indicador de carga accesible y centrado
 * 
 * @example
 * // Uso básico durante carga de datos
 * const SearchComponent = () => {
 *   const [isLoading, setIsLoading] = useState(false);
 *   const [results, setResults] = useState([]);
 * 
 *   const handleSearch = async (query) => {
 *     setIsLoading(true);
 *     try {
 *       const data = await searchAPI(query);
 *       setResults(data);
 *     } finally {
 *       setIsLoading(false);
 *     }
 *   };
 * 
 *   return (
 *     <div>
 *       <SearchForm onSearch={handleSearch} />
 *       {isLoading ? <Loading /> : <Results data={results} />}
 *     </div>
 *   );
 * };
 * 
 * @example
 * // Integración con BuscadorApp
 * const BuscadorApp = () => {
 *   const [showLoading, setShowLoading] = useState(true);
 *   
 *   return (
 *     <div className="container">
 *       <SearchForm />
 *       {showLoading && <Loading />}
 *       {!showLoading && <Results />}
 *     </div>
 *   );
 * };
 * 
 * @example
 * // Estados condicionales múltiples
 * const DataComponent = ({ isSearching, isSubmitting, isNavigating }) => {
 *   const showLoader = isSearching || isSubmitting || isNavigating;
 *   
 *   return (
 *     <div>
 *       {showLoader && <Loading />}
 *       <MainContent />
 *     </div>
 *   );
 * };
 * 
 * @example
 * // Patrón de carga con timeout
 * const SearchResults = () => {
 *   const [loading, setLoading] = useState(false);
 *   
 *   useEffect(() => {
 *     setLoading(true);
 *     
 *     // Simular carga con timeout mínimo para UX
 *     Promise.all([
 *       fetchData(),
 *       new Promise(resolve => setTimeout(resolve, 500))
 *     ]).finally(() => setLoading(false));
 *   }, []);
 *   
 *   return loading ? <Loading /> : <Content />;
 * };
 * 
 * @example
 * // Estructura HTML generada:
 * <div className="loading" role="status" aria-live="polite">
 *   <div className="container-loading">
 *     <div className="loadingIndicator" aria-hidden="true"></div>
 *   </div>
 * </div>
 * 
 * @since 1.0.0
 * @author Valeria Garcia
 * 
 * @see {@link ../BuscadorApp.js} Componente principal que utiliza Loading
 * @see {@link ../results/results.js} Componente que se muestra después de Loading
 * @see {@link https://www.w3.org/WAI/ARIA/apg/patterns/alert/} Guía ARIA para elementos de estado
 * @see {@link https://www.w3.org/WAI/WCAG21/Understanding/status-messages.html} WCAG 2.1 - Mensajes de estado
 */
export default function Loading() {
  return (
    <div className="loading" role="status" aria-live="polite">
      <div className="container-loading">
        <div className="loadingIndicator" aria-hidden="true"></div>
      </div>
    </div>
  );
}
